import { applyZoom, zoomRangeOf } from "../camera-lens";
import { isDefaultFraming, planFraming, sameFraming, type CameraFraming, type FramingPlan } from "./camera-framing";

/**
 * La caméra d'un appareil, cadrée avant son encodeur.
 *
 * # Pourquoi ici, et pas à l'affichage
 *
 * Parce que c'est le seul endroit où ce qu'on écarte du champ disparaît
 * vraiment. Les pixels rognés ici n'atteignent ni l'encodeur, ni la salle, ni
 * le SFU, ni l'enregistrement — donc pas les MP4 du replay, qui sont servis à
 * qui a le lien. Un recadrage posé en CSS chez qui regarde aurait laissé
 * l'image entière chez chaque spectateur, à un `transform` retiré dans
 * l'inspecteur près. Voir `docs/spec/video-engine.md`.
 *
 * # Le cas courant ne coûte rien
 *
 * Tant que le cadrage ne demande ni découpe ni quart de tour — et c'est
 * l'immense majorité des récitals —, c'est la piste de la caméra elle-même qui
 * se publie : aucune toile, aucune boucle de dessin. Le zoom optique en fait
 * partie : il se pose sur la piste, qui continue de se publier telle quelle.
 *
 * Passer d'un régime à l'autre **change la piste à publier**, et c'est à
 * l'appelant de la republier (`onTrack`). Un `replaceTrack` suffit : LiveKit
 * garde la publication, et seule la source change.
 *
 * # Ce que la toile coûte quand même
 *
 * Une image de retard, et le temps de la dessiner. La date que porte chaque
 * image est posée sur l'émetteur RTP, en aval de la toile : elle dit donc
 * l'instant où l'image est entrée dans l'encodeur, et non celui où le capteur
 * l'a prise. L'écart est d'une image de caméra — 33 ms à 30 i/s — et il est
 * constant, donc il se paie une fois dans le calage, pas à chaque image.
 */
export class FramedCamera {
  private readonly source: MediaStreamTrack;
  private readonly onTrack: (track: MediaStreamTrack) => void;

  private framing: CameraFraming | null = null;
  private plan: FramingPlan | null = null;

  /** La toile et sa source d'images, montées seulement quand on rogne. */
  private video: HTMLVideoElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;
  private stream: MediaStream | null = null;
  private frame = 0;
  private drawing = false;
  private stopped = false;

  constructor(source: MediaStreamTrack, onTrack: (track: MediaStreamTrack) => void) {
    this.source = source;
    this.onTrack = onTrack;
  }

  /** La piste à publier : celle de la caméra, ou celle de la toile. */
  track(): MediaStreamTrack {
    return this.stream?.getVideoTracks()[0] ?? this.source;
  }

  /**
   * Pose un cadrage. Le zoom optique part sur la piste, le reste sur la toile.
   *
   * Sans effet quand le cadrage n'a pas changé : un état de séance se republie
   * pour bien d'autres raisons que le cadrage — une position, une audience —,
   * et rouvrir la toile à chacune ferait clignoter l'image de tout le monde.
   */
  async setFraming(framing: CameraFraming): Promise<void> {
    // Le premier cadrage neutre ne demande rien de plus que l'absence de
    // cadrage : inutile de monter quoi que ce soit pour lui.
    const unchanged = this.framing === null ? isDefaultFraming(framing) : sameFraming(this.framing, framing);

    if (this.stopped || unchanged) {
      return;
    }

    this.framing = framing;

    await this.apply(framing);
  }

  /**
   * Pose ce cadrage sur les dimensions que la caméra a **maintenant**.
   *
   * Séparée de {@link FramedCamera.setFraming} parce qu'elle se rejoue sans
   * qu'un cadrage ait changé : un téléphone qu'on tourne échange les côtés de
   * sa capture, et une toile calculée une fois pour toutes garderait l'ancienne
   * forme — l'image y rentrerait de travers, et l'écran qui la reçoit ne
   * saurait même pas qu'elle a tourné. Voir le `resize` de {@link
   * FramedCamera.draw}.
   */
  private async apply(framing: CameraFraming): Promise<void> {
    const plan = planFraming(this.sourceSize(), framing, opticalRange(this.source));

    if (plan.opticalZoom !== null) {
      // Un refus ne fait pas tomber le reste : l'appareil garde le zoom qu'il
      // avait, et la découpe se pose quand même.
      await applyZoom(this.source, plan.opticalZoom).catch(() => undefined);
    }

    // Une source sans dimensions connues ne se rogne pas : on ne sait pas où
    // découper. Elle se publie telle quelle plutôt que de partir sur une toile
    // vide.
    if (plan.passthrough || plan.draw === null) {
      this.plan = plan;
      this.release();

      return;
    }

    const started = this.stream === null;

    this.plan = plan;
    this.draw(plan);

    if (started) {
      this.onTrack(this.track());
    }
  }

  /**
   * Les dimensions de ce que filme la caméra.
   *
   * L'élément vidéo d'abord, quand il y en a un : c'est lui qui décode, et son
   * `videoWidth` est ce qu'on va dessiner. Les réglages de la piste ensuite —
   * ils disent la même chose, mais on ne les a pas toujours vus bouger.
   */
  private sourceSize(): { width: number; height: number } {
    const video = this.video;

    if (video !== null && video.videoWidth > 0 && video.videoHeight > 0) {
      return { width: video.videoWidth, height: video.videoHeight };
    }

    const settings = this.source.getSettings();

    return { width: settings.width ?? 0, height: settings.height ?? 0 };
  }

  /** Arrête la toile ; la piste de la caméra, elle, appartient à l'appelant. */
  stop(): void {
    this.stopped = true;
    this.release();
  }

  /** Monte la toile s'il le faut, la redimensionne, et lance la boucle. */
  private draw(plan: FramingPlan): void {
    const canvas = this.canvas ?? document.createElement("canvas");

    canvas.width = plan.output.width;
    canvas.height = plan.output.height;

    if (this.canvas === null) {
      this.canvas = canvas;
      // `alpha: false` : l'image d'une caméra est opaque, et la composition
      // sans couche alpha est la moins chère des deux.
      this.context = canvas.getContext("2d", { alpha: false });
      // Sans argument : la toile publie une image à chaque fois qu'elle change,
      // donc exactement une par image de caméra dessinée. Une cadence imposée
      // ici doublerait ou jetterait des images.
      this.stream = canvas.captureStream();
    }

    if (this.video === null) {
      const video = document.createElement("video");

      video.srcObject = new MediaStream([this.source]);
      video.muted = true;
      video.playsInline = true;
      // La caméra change de forme en cours de route — on tourne le téléphone,
      // ou WebKit rouvre la capture — et la toile doit se refaire à ces
      // dimensions-là. L'événement est celui de l'élément vidéo, pas celui de
      // la fenêtre.
      video.addEventListener("resize", () => {
        const framing = this.framing;

        if (!this.stopped && framing !== null) {
          void this.apply(framing);
        }
      });
      // Hors du document : rien ne l'affiche, il n'est là que pour décoder.
      void video.play().catch(() => undefined);

      this.video = video;
    }

    this.loop();
  }

  /**
   * Dessine à chaque image de la caméra.
   *
   * `requestVideoFrameCallback` plutôt que l'horloge d'animation : on veut une
   * image dessinée par image captée, ni plus — redessiner soixante fois par
   * seconde une caméra qui en donne trente ferait le double de travail pour la
   * même vidéo.
   */
  private loop(): void {
    const video = this.video;

    if (video === null || this.drawing) {
      return;
    }

    this.drawing = true;

    const paint = () => {
      if (this.stopped || this.video !== video) {
        this.drawing = false;

        return;
      }

      this.paint();
      this.schedule(video, paint);
    };

    this.schedule(video, paint);
  }

  private schedule(video: HTMLVideoElement, paint: () => void): void {
    const target = video as HTMLVideoElement & {
      requestVideoFrameCallback?: (callback: () => void) => number;
    };

    if (typeof target.requestVideoFrameCallback === "function") {
      this.frame = target.requestVideoFrameCallback(paint);

      return;
    }

    this.frame = requestAnimationFrame(paint);
  }

  /**
   * Une image : l'image de la source, tournée, avec le point choisi ramené au
   * centre de la toile.
   *
   * # Aucune mise à l'échelle
   *
   * Les pixels de la source arrivent au format 1:1 ; c'est la toile, plus
   * petite, qui fait le zoom. L'encodeur reçoit donc moins de pixels à coder,
   * au lieu de pixels grossis.
   *
   * # L'ordre des transformations
   *
   * Sur une toile, la dernière posée s'applique la première au dessin :
   * `translate(centre)` puis `rotate` puis `translate(-c)` envoie un point `p`
   * de la source sur `centre + R(angle)·(p - c)`. C'est exactement ce qu'on
   * veut, et le sens des aiguilles est celui de `rotate` dans un repère dont
   * l'axe des y descend — celui de la toile.
   */
  private paint(): void {
    const context = this.context;
    const canvas = this.canvas;
    const video = this.video;
    const draw = this.plan?.draw;

    if (context === null || canvas === null || video === null || draw == null) {
      return;
    }

    context.save();
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate(draw.angle);
    // Sous 1 quand la découpe dépasse le plafond de publication : on capte
    // plus large qu'on ne publie, et l'excédent se réduit ici plutôt que de
    // partir dans l'encodeur.
    context.scale(draw.scale, draw.scale);
    context.translate(-draw.cx, -draw.cy);
    context.drawImage(video, 0, 0);
    context.restore();
  }

  /** Rend la toile et sa source d'images ; la piste publiée redevient la caméra. */
  private release(): void {
    if (this.stream === null) {
      return;
    }

    cancelFrame(this.video, this.frame);
    this.drawing = false;

    for (const track of this.stream.getTracks()) {
      track.stop();
    }

    if (this.video !== null) {
      this.video.srcObject = null;
      this.video = null;
    }

    this.stream = null;
    this.canvas = null;
    this.context = null;

    if (!this.stopped) {
      this.onTrack(this.source);
    }
  }
}

function cancelFrame(video: HTMLVideoElement | null, handle: number): void {
  const target = video as (HTMLVideoElement & { cancelVideoFrameCallback?: (handle: number) => void }) | null;

  if (target !== null && typeof target.cancelVideoFrameCallback === "function") {
    target.cancelVideoFrameCallback(handle);

    return;
  }

  cancelAnimationFrame(handle);
}

/** L'étendue du zoom de la piste, quand elle en a une complète. */
function opticalRange(track: MediaStreamTrack): { min: number; max: number } | null {
  const range = zoomRangeOf(track);

  if (range?.min === undefined || range.max === undefined) {
    return null;
  }

  return { min: range.min, max: range.max };
}
